/* global google */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { GoogleMap, useLoadScript } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { auth } from '../firebase';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import {
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Drawer,
    List,
    ListItem,
    Checkbox,
    FormControlLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import geocoded_cache_mp_de from '../data/geocoded_cache_mp_de.json';
import geocoded_cache_sou_de from '../data/geocoded_cache_sou_de.json';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

// Map labels to their corresponding data
const geocodeCacheMap = {
    mp_de: geocoded_cache_mp_de,
    sou_de: geocoded_cache_sou_de,
};

const labelTranslations = {
    mp_de: "Marsden Park DE",
    sou_de: "Southlands DE",
};

const Map = () => {
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    });

    // State variables
    const [mapInstance, setMapInstance] = useState(null); // Map instance
    const [markerClusterer, setMarkerClusterer] = useState(null); // Marker clusterer
    const [markersByLabel, setMarkersByLabel] = useState({}); // Markers grouped by label
    const [filteredMarkers, setFilteredMarkers] = useState([]); // Markers to display
    const [checkboxState, setCheckboxState] = useState({}); // Checkbox states
    const [drawerOpen, setDrawerOpen] = useState(false); // Drawer open/close state
    const infoWindowRef = useRef(null); // Use ref instead of state
    const [showLabels, setShowLabels] = useState(false); // Show/hide marker labels
    const [geocodeCaches, setGeocodeCaches] = useState([]); // Imported geocoded data
    const navigate = useNavigate(); // Initialize navigate
    const isMapLoadedRef = useRef(false);
    const initialCenter = useMemo(() => ({ lat: -32.729847, lng: 147.661172 }), []);
    const [openInfoWindows, setOpenInfoWindows] = useState([]);
    const CACHE_VERSION = '28.10.24'; // Update this when data or structure changes

    // **1. Move 'fetchGeocodeCachesFromFirestore' before 'loadAndCacheGeocodeData'**
    const fetchGeocodeCachesFromFirestore = useCallback(async () => {
        const user = auth.currentUser;
        if (user) {
            const db = getFirestore();
            const userDocRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
                const datasets = userDoc.data().assignedDataset || [];
                const caches = datasets
                    .map((label) => {
                        const data = geocodeCacheMap[label.toLowerCase()];
                        if (data && data.addresses) {
                            return { label, data: data.addresses };
                        } else {
                            console.warn(`No data found for label: ${label}`);
                            return null;
                        }
                    })
                    .filter(Boolean);
                setGeocodeCaches(caches);
                localStorage.setItem('geocodeCaches', JSON.stringify(caches));
                localStorage.setItem('geocodeCachesVersion', CACHE_VERSION); // Save version here
            }
        }
    }, []);


    useEffect(() => {
        console.log('Current User:', auth.currentUser);
    }, []);


    const loadAndCacheGeocodeData = useCallback(async () => {
        const cachedData = localStorage.getItem('geocodeCaches');
        const cachedVersion = localStorage.getItem('geocodeCachesVersion');

        if (cachedData && cachedVersion === CACHE_VERSION) {
            try {
                const parsedData = JSON.parse(cachedData);
                const isValid =
                    Array.isArray(parsedData) &&
                    parsedData.every((cache) => cache && cache.label && cache.data);
                if (isValid) {
                    setGeocodeCaches(parsedData);
                } else {
                    console.warn('Invalid cached data detected. Clearing localStorage.');
                    localStorage.removeItem('geocodeCaches');
                    localStorage.removeItem('geocodeCachesVersion');
                    await fetchGeocodeCachesFromFirestore();
                }
            } catch (error) {
                console.error('Error parsing cached data:', error);
                localStorage.removeItem('geocodeCaches');
                localStorage.removeItem('geocodeCachesVersion');
                await fetchGeocodeCachesFromFirestore();
            }
        } else {
            // Fetch fresh data and cache it with the updated version
            await fetchGeocodeCachesFromFirestore();
            localStorage.setItem('geocodeCachesVersion', CACHE_VERSION);
        }
    }, [fetchGeocodeCachesFromFirestore]);


    useEffect(() => {
        loadAndCacheGeocodeData();
    }, [loadAndCacheGeocodeData]);

    useEffect(() => {
        // Log the current cache version
        console.log('Current CACHE_VERSION:', CACHE_VERSION);

        // Log the version stored in localStorage (if any)
        const storedCacheVersion = localStorage.getItem('geocodeCachesVersion');
        console.log('Stored cache version in localStorage:', storedCacheVersion);

        loadAndCacheGeocodeData();
    }, [loadAndCacheGeocodeData]);


    // **2. Optimize Marker Creation with useMemo**
    const processedMarkers = useMemo(() => {
        const newMarkersByLabel = {};
        const initialCheckboxState = {};

        geocodeCaches.forEach((cache) => {
            if (!cache || !cache.label) return;
            const { label, data } = cache;
            if (data && Array.isArray(data)) {
                if (!newMarkersByLabel[label]) {
                    newMarkersByLabel[label] = {};
                    initialCheckboxState[label] = {};
                }
                data.forEach((entry) => {
                    const { worksheet, year, lat, lng, name } = entry;
                    if (!newMarkersByLabel[label][worksheet]) {
                        newMarkersByLabel[label][worksheet] = {};
                        initialCheckboxState[label][worksheet] = {};
                    }
                    if (!newMarkersByLabel[label][worksheet][year]) {
                        newMarkersByLabel[label][worksheet][year] = [];
                        initialCheckboxState[label][worksheet][year] = false;
                    }
                    const marker = {
                        position: { lat, lng },
                        title: `${name} (${year}) - ${worksheet}`,
                        labelContent: `${name} (${year}) - ${worksheet}`,
                    };
                    newMarkersByLabel[label][worksheet][year].push(marker);
                });
            }
        });

        return { markersByLabel: newMarkersByLabel, initialCheckboxState };
    }, [geocodeCaches]);

    useEffect(() => {
        setMarkersByLabel(processedMarkers.markersByLabel);
        setCheckboxState(processedMarkers.initialCheckboxState);
    }, [processedMarkers]);

    // **3. Memoize event handlers with useCallback**
    const handleCheckboxChange = useCallback(
        (label, worksheet, year) => {
            setCheckboxState((prevState) => ({
                ...prevState,
                [label]: {
                    ...prevState[label],
                    [worksheet]: {
                        ...prevState[label]?.[worksheet],
                        [year]: !prevState[label]?.[worksheet]?.[year],
                    },
                },
            }));
        },
        []
    );

    // **4. Apply checkbox filters**
    useEffect(() => {
        const filtered = [];
        Object.keys(markersByLabel).forEach((label) => {
            if (markersByLabel[label]) {
                Object.keys(markersByLabel[label]).forEach((worksheet) => {
                    if (markersByLabel[label][worksheet]) {
                        Object.keys(markersByLabel[label][worksheet]).forEach((year) => {
                            if (
                                checkboxState[label]?.[worksheet]?.[year] &&
                                markersByLabel[label][worksheet][year]
                            ) {
                                filtered.push(markersByLabel[label][worksheet][year]);
                            }
                        });
                    }
                });
            }
        });
        setFilteredMarkers(filtered.flat());
        console.log('Filtered Markers:', filtered.flat());
    }, [markersByLabel, checkboxState]);

    // **6. Ensure mapInstance is stable**
    const handleMapLoad = useCallback((map) => {
        setMapInstance(map);
        console.log('Map instance set:', map);
    }, []);


    // **7. Memoize handleMarkerClick**
    const handleMarkerClick = useCallback(
        (marker, mapMarker) => {
            if (infoWindowRef.current) {
                infoWindowRef.current.close();
            }
            infoWindowRef.current = new google.maps.InfoWindow({
                content: marker.title,
                position: marker.position,
                disableAutoPan: true, // Prevents auto-panning
            });
            infoWindowRef.current.open(mapInstance, mapMarker);
        },
        [mapInstance]
    );

    // Memoize googleMarkers
    const googleMarkers = useMemo(() => {
        const markers = filteredMarkers.map((marker) => {
            const mapMarker = new google.maps.Marker({
                position: marker.position,
                title: marker.title,
                // No label property
            });

            mapMarker.addListener('click', () => handleMarkerClick(marker, mapMarker));

            return mapMarker;
        });
        console.log('Google Markers:', markers);
        return markers;
    }, [filteredMarkers, handleMarkerClick]);

    // Update MarkerClusterer efficiently
    useEffect(() => {
        if (mapInstance) {
            if (markerClusterer) {
                markerClusterer.clearMarkers();
            }

            if (googleMarkers.length > 0) {
                const newClusterer = new MarkerClusterer({
                    map: mapInstance,
                    markers: googleMarkers,
                });

                setMarkerClusterer(newClusterer);
            }
        }
    }, [mapInstance, googleMarkers]);

// Toggle function to show/hide labels
    const toggleLabels = useCallback(() => {
        setShowLabels((prev) => !prev);

        // Open or close InfoWindows for markers based on showLabels
        googleMarkers.forEach((mapMarker, index) => {
            if (showLabels) {
                const infoWindow = new google.maps.InfoWindow({
                    content: filteredMarkers[index].title,
                    position: filteredMarkers[index].position,
                    disableAutoPan: true,
                });
                infoWindow.open(mapInstance, mapMarker);
                openInfoWindows.push(infoWindow);
            } else {
                openInfoWindows.forEach((infoWindow) => infoWindow.close());
                setOpenInfoWindows([]);
            }
        });
    }, [showLabels, googleMarkers, filteredMarkers, mapInstance, openInfoWindows]);

    // Effect to handle InfoWindows when showLabels changes
    useEffect(() => {
        if (mapInstance && googleMarkers.length > 0) {
            // Close all existing InfoWindows first
            openInfoWindows.forEach((infoWindow) => infoWindow.close());

            if (showLabels) {
                // Open InfoWindows for all markers when labels are shown
                const infoWindows = googleMarkers.map((mapMarker, index) => {
                    const marker = filteredMarkers[index];
                    const infoWindow = new google.maps.InfoWindow({
                        content: marker.title,
                        position: marker.position,
                        disableAutoPan: true, // Prevents map from panning
                    });
                    infoWindow.open(mapInstance, mapMarker);
                    return infoWindow;
                });

                // Replace openInfoWindows with the newly created InfoWindows array
                setOpenInfoWindows(infoWindows);
            } else {
                // Clear openInfoWindows if labels are hidden
                setOpenInfoWindows([]);
            }
        }
    }, [showLabels, mapInstance, googleMarkers, filteredMarkers]);

// Effect to handle clusters and respect showLabels
    useEffect(() => {
        if (markerClusterer && googleMarkers.length > 0) {
            const handleClusterChange = () => {
                openInfoWindows.forEach((infoWindow) => infoWindow.close());
                setOpenInfoWindows([]);

                // Open InfoWindows only for markers visible after clustering
                if (showLabels) {
                    googleMarkers.forEach((mapMarker, index) => {
                        if (mapMarker.getMap() === mapInstance) { // Check if marker is visible
                            const infoWindow = new google.maps.InfoWindow({
                                content: filteredMarkers[index].title,
                                position: filteredMarkers[index].position,
                                disableAutoPan: true,
                            });
                            infoWindow.open(mapInstance, mapMarker);
                            openInfoWindows.push(infoWindow);
                        }
                    });
                }
            };

            // Attach the clusteringend listener
            markerClusterer.addListener("clusteringend", handleClusterChange);

            // Cleanup listener on unmount
            return () => {
                google.maps.event.clearListeners(markerClusterer, "clusteringend");
            };
        }
    }, [markerClusterer, googleMarkers, showLabels, filteredMarkers, mapInstance, openInfoWindows]);


    // **9. Memoize other functions**
    const showAllYears = useCallback(
        (label, worksheet) => {
            setCheckboxState((prevState) => ({
                ...prevState,
                [label]: {
                    ...prevState[label],
                    [worksheet]: Object.keys(markersByLabel[label][worksheet]).reduce((acc, year) => {
                        acc[year] = true;
                        return acc;
                    }, {}),
                },
            }));
        },
        [markersByLabel]
    );

    const hideAllYears = useCallback(
        (label, worksheet) => {
            setCheckboxState((prevState) => ({
                ...prevState,
                [label]: {
                    ...prevState[label],
                    [worksheet]: Object.keys(markersByLabel[label][worksheet]).reduce((acc, year) => {
                        acc[year] = false;
                        return acc;
                    }, {}),
                },
            }));
        },
        [markersByLabel]
    );

    const handleLogout = useCallback(async () => {
        try {
            await signOut(auth);
            console.log('User signed out successfully');
            navigate('/login'); // Navigate back to the login page after logout
        } catch (error) {
            console.error('Error signing out:', error);
        }
    }, [navigate]);

    // Drawer content
    const drawerList = useCallback(
        () => (
            <div>
                <List>
                    {geocodeCaches.map((cache) => (
                        <div key={cache.label}>
                            <Typography variant="h6" style={{ padding: '10px' }}>
                                {labelTranslations[cache.label] || cache.label}
                            </Typography>
                            {markersByLabel[cache.label] &&

                                Object.keys(markersByLabel[cache.label]).map((worksheet) => (
                                    <Accordion key={`${cache.label}-${worksheet}`}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls={`${cache.label}-${worksheet}-content`}
                                            id={`${cache.label}-${worksheet}-header`}
                                        >
                                            <Typography variant="subtitle1">{worksheet}</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    marginBottom: '10px',
                                                }}
                                            >
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() => showAllYears(cache.label, worksheet)}
                                                >
                                                    Show All
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() => hideAllYears(cache.label, worksheet)}
                                                >
                                                    Hide All
                                                </Button>
                                            </div>
                                            <List>
                                                {Object.keys(markersByLabel[cache.label][worksheet]).map((year) => (
                                                    <ListItem key={`${cache.label}-${worksheet}-${year}`}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={
                                                                        checkboxState[cache.label]?.[worksheet]?.[year] ||
                                                                        false
                                                                    }
                                                                    onChange={() =>
                                                                        handleCheckboxChange(cache.label, worksheet, year)
                                                                    }
                                                                />
                                                            }
                                                            label={year}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </AccordionDetails>
                                        <Divider />
                                    </Accordion>
                                ))}

                        </div>
                    ))}
                </List>
                {/* Logout button */}
                <div style={{ padding: '10px', textAlign: 'center' }}>
                    <Button variant="outlined" color="secondary" onClick={handleLogout}>
                        Logout
                    </Button>
                    <p>Last Updated: {CACHE_VERSION}</p>
                </div>
            </div>
        ),
        [geocodeCaches, markersByLabel, checkboxState, showAllYears, hideAllYears, handleCheckboxChange, handleLogout]
    );

    if (!isLoaded || !geocodeCaches.length) {
        return <div>Loading map...</div>;
    }


    return (
        <div>
            <AppBar position="static">
                <Toolbar>
                    <Typography variant="body1" component="div" sx={{ flexGrow: 1 }}>
                        Select a year group from the menu:
                    </Typography>
                    <Button
                        variant="outlined"
                        color="inherit"
                        onClick={toggleLabels}
                    >
                        {showLabels ? 'Hide Labels' : 'Show Labels'}
                    </Button>

                    <IconButton
                        edge="end"
                        color="inherit"
                        aria-label="menu"
                        onClick={() => {
                            setDrawerOpen(true);
                        }}
                    >
                        <MenuIcon />
                    </IconButton>


                </Toolbar>
            </AppBar>

            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => {
                    setDrawerOpen(false);
                }}
            >
                {drawerList()}
            </Drawer>


            <div id="map-container">
                <GoogleMap
                    mapContainerStyle={{
                        width: '100%',
                        height: 'calc(100vh - 70px)',
                        minHeight: '600px',
                    }}
                    center={initialCenter}
                    zoom={7} // And this line
                    onLoad={handleMapLoad}
                    options={{
                        streetViewControl: false,
                        mapTypeControl: false,
                    }}
                />


            </div>
        </div>
    );
};

export default Map;
